import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  User, Trash2, Download,
  Eye, EyeOff, Check, AlertTriangle,
  Calendar, Users, Plus, UserPlus, LogOut, Mail, Bell,
  X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Palette, ListTodo, Database, Smartphone, Hash
} from 'lucide-react';
import { Tag as TextTag } from '../../hooks/useTags';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSpace } from '../../contexts/SpaceContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { NotificationPreferences } from '../../hooks/useNotificationPreferences';
import { useTimezone, getBrowserTimezone } from '../../hooks/useTimezone';
import { useGoogleCalendarSync } from '../../hooks/useGoogleCalendarSync';
import { ConfirmDialog, type ConfirmDialogState } from '../ui/ConfirmDialog';
import { supabase } from '../../config/supabase';
import { useToast } from '../../contexts/ToastContext';
import { Calendar as CalendarType } from '../../types';
import { CalendarEvent } from '../../hooks/useSupabaseEvents';
import { COLORS, isHexColor } from '../../utils/calendar/constants';
import { ColorPicker } from '../ui/ColorPicker';

interface SettingsPageProps {
  onClose: () => void;
  onOpenShare: () => void;
  onOpenImportExport: () => void;
  showPastDaysGreyed: boolean;
  onShowPastDaysGreyedChange: (value: boolean) => void;
  maxEventRows: number;
  onMaxEventRowsChange: (value: number) => void;
  autoArchiveEnabled: boolean;
  autoArchiveDays: number;
  onAutoArchiveEnabledChange: (value: boolean) => void;
  onAutoArchiveDaysChange: (value: number) => void;
  calendars: CalendarType[];
  events: CalendarEvent[];
  onAddTag: (tag: { name: string; color: string }) => Promise<void>;
  onUpdateTag: (id: string, updates: Partial<CalendarType>) => Promise<void>;
  onDeleteTag: (id: string) => Promise<void>;
  onEventUpdate: (id: string | number, updates: Partial<CalendarEvent>) => Promise<any>;
  refetchTags: () => Promise<void>;
  reorderCalendars: (fromIndex: number, toIndex: number) => Promise<void>;
  selectedCalendar: CalendarType | null;
  setSelectedCalendar: (tag: CalendarType | null) => void;
  showTodosOnCalendar: boolean;
  onShowTodosOnCalendarChange: (value: boolean) => void;
  // Text tags (shared with Calendar's spaceTags instance for sync)
  textTags: TextTag[];
  textTagsLoading: boolean;
  onCreateTextTag: (name: string) => Promise<TextTag | null>;
  onDeleteTextTag: (id: number) => Promise<void>;
  onRenameTextTag: (id: number, name: string) => Promise<void>;
  initialSection?: SettingsSection;
}

type SettingsSection = 'account' | 'appearance' | 'calendar' | 'notifications' | 'tasks' | 'spaces' | 'tags' | 'data';
type NavItem = { id: string; label: string };

/* ── Reusable primitives ── */

const Toggle: React.FC<{ enabled: boolean; onToggle: () => void }> = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${
      enabled ? 'bg-[var(--accent-primary)]' : 'bg-[var(--surface-tertiary)]'
    }`}
  >
    <div className={`absolute top-[3px] w-3 h-3 rounded-full bg-white transition-transform ${
      enabled ? 'translate-x-[17px]' : 'translate-x-[3px]'
    }`} />
  </button>
);

const Checkbox: React.FC<{ checked: boolean; disabled?: boolean; onToggle: () => void }> = ({
  checked,
  disabled,
  onToggle,
}) => (
  <button
    onClick={disabled ? undefined : onToggle}
    role="checkbox"
    aria-checked={checked}
    className={`w-[18px] h-[18px] rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-colors ${
      disabled
        ? 'border-[var(--border-primary)] bg-transparent opacity-30 cursor-default'
        : checked
        ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)] hover:opacity-90'
        : 'border-[var(--border-primary)] bg-transparent hover:border-[var(--accent-primary)]'
    }`}
  >
    {checked && !disabled && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const selectClass = 'px-3 py-1.5 bg-[var(--input-bg)] text-[var(--content-primary)] rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] appearance-none cursor-pointer pr-7 bg-no-repeat bg-[length:10px] bg-[position:right_8px_center]';
const selectChevronStyle: React.CSSProperties = { backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")` };

const SettingRow: React.FC<{
  label: string;
  description?: string;
  children: React.ReactNode;
  last?: boolean;
  disabled?: boolean;
}> = ({ label, description, children, last, disabled }) => (
  <div className={`flex items-center justify-between py-3.5 gap-4 ${last ? '' : 'border-b border-[var(--border-primary)]'} ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
    <div className="min-w-0">
      <p className="text-[13px] text-[var(--content-primary)]">{label}</p>
      {description && <p className="text-xs text-[var(--content-tertiary)] mt-0.5">{description}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[13px] font-medium text-[var(--content-secondary)] mb-1 mt-6 first:mt-0">
    {children}
  </p>
);

const ManageRow: React.FC<{
  label: string;
  description?: string;
  buttonLabel: string;
  onClick: () => void;
}> = ({ label, description, buttonLabel, onClick }) => (
  <div className="flex items-center justify-between py-3.5 border-b border-[var(--border-primary)]">
    <div className="min-w-0">
      <p className="text-[13px] text-[var(--content-primary)]">{label}</p>
      {description && <p className="text-xs text-[var(--content-tertiary)] mt-0.5">{description}</p>}
    </div>
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-3 py-1.5 text-[13px] font-medium text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] rounded-lg transition-colors flex-shrink-0"
    >
      {buttonLabel}
      <ChevronRight className="w-3.5 h-3.5" />
    </button>
  </div>
);

type TestState = 'idle' | 'sending' | 'sent' | 'error';

function testBtnClass(state: TestState) {
  return `flex-1 py-2 px-3 text-[13px] font-medium rounded-lg border transition-colors ${
    state === 'sent'    ? 'border-[var(--success)] text-[var(--success)]' :
    state === 'error'   ? 'border-[var(--error)] text-[var(--error)]' :
    state === 'sending' ? 'border-[var(--border-primary)] text-[var(--content-tertiary)]' :
    'border-[var(--border-primary)] text-[var(--content-primary)] hover:bg-[var(--surface-hover)]'
  }`;
}

function testBtnLabel(state: TestState, label: string) {
  return state === 'sending' ? '···' : state === 'sent' ? 'Sent' : state === 'error' ? 'Error' : label;
}

function NotifRow({
  label,
  emailKey,
  pushKey,
  preferences,
  isSubscribed,
  pushSupported,
  updatePreference,
}: {
  label: string;
  emailKey: keyof NotificationPreferences;
  pushKey: keyof NotificationPreferences;
  preferences: NotificationPreferences;
  isSubscribed: boolean;
  pushSupported: boolean;
  updatePreference: (key: keyof NotificationPreferences, value: boolean) => Promise<void>;
}) {
  return (
    <div className="flex items-center py-2.5 gap-2">
      <span className="flex-1 text-[13px] text-[var(--content-primary)]">{label}</span>
      <div className="w-14 flex justify-center">
        <Checkbox
          checked={!!preferences[emailKey]}
          disabled={!preferences.emailEnabled}
          onToggle={() => updatePreference(emailKey, !preferences[emailKey] as boolean)}
        />
      </div>
      <div className="w-14 flex justify-center">
        {!pushSupported ? (
          <span className="text-[11px] text-[var(--content-tertiary)]">—</span>
        ) : (
          <Checkbox
            checked={isSubscribed && !!preferences[pushKey]}
            disabled={!isSubscribed || !preferences.pushEnabled}
            onToggle={() => updatePreference(pushKey, !preferences[pushKey] as boolean)}
          />
        )}
      </div>
    </div>
  );
}

function NotificationsSection({
  selectClass,
  selectChevronStyle,
}: {
  selectClass: string;
  selectChevronStyle: React.CSSProperties;
}) {
  const { user } = useAuth();
  const { preferences, loading, updatePreference } = useNotifications();
  const { pushSupported, isSubscribed, subscribeToPush } = usePushNotifications();
  const [emailTestState, setEmailTestState] = useState<TestState>('idle');
  const [pushTestState, setPushTestState] = useState<TestState>('idle');

  if (loading || !preferences) {
    return <div className="text-center py-8 text-[var(--content-secondary)]">Loading...</div>;
  }

  const handlePushMasterToggle = async () => {
    if (!isSubscribed) {
      // Not yet subscribed — need browser permission (inherently async)
      const ok = await subscribeToPush();
      if (ok) await updatePreference('pushEnabled', true);
    } else {
      // Already subscribed — just flip the preference (optimistic, instant)
      await updatePreference('pushEnabled', !preferences.pushEnabled);
    }
  };

  const sendEmailTest = async () => {
    if (!user?.email || emailTestState !== 'idle') return;
    setEmailTestState('sending');
    try {
      const { error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: 'notification_test',
          recipientEmail: user.email,
          recipientName: user.user_metadata?.display_name || user.email,
          actorName: '',
          spaceName: '',
        },
      });
      setEmailTestState(error ? 'error' : 'sent');
    } catch {
      setEmailTestState('error');
    } finally {
      setTimeout(() => setEmailTestState('idle'), 3000);
    }
  };

  const sendPushTest = async () => {
    if (pushTestState !== 'idle') return;
    setPushTestState('sending');
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification('Test notification', {
        body: 'Your push notifications are working correctly.',
        tag: 'mozayc-test',
      });
      setPushTestState('sent');
    } catch {
      setPushTestState('error');
    } finally {
      setTimeout(() => setPushTestState('idle'), 3000);
    }
  };

  const rowProps = { preferences, isSubscribed, pushSupported, updatePreference };

  return (
    <div>
      {/* Column headers */}
      <div className="flex items-center gap-2 pb-2 border-b border-[var(--border-primary)]">
        <div className="flex-1" />
        <div className="w-14 flex flex-col items-center gap-1">
          <span className="text-[11px] font-medium text-[var(--content-tertiary)] uppercase tracking-wider">Email</span>
          <Checkbox
            checked={preferences.emailEnabled}
            onToggle={() => updatePreference('emailEnabled', !preferences.emailEnabled)}
          />
        </div>
        <div className="w-14 flex flex-col items-center gap-1">
          <span className="text-[11px] font-medium text-[var(--content-tertiary)] uppercase tracking-wider">Web</span>
          {!pushSupported ? (
            <span className="text-[11px] text-[var(--content-tertiary)] leading-[18px]">N/A</span>
          ) : (
            <Checkbox
              checked={isSubscribed && preferences.pushEnabled}
              onToggle={handlePushMasterToggle}
            />
          )}
        </div>
      </div>

      {/* Digest frequency */}
      {preferences.emailEnabled && (
        <SettingRow label="Digest frequency" description="How often to receive email summaries">
          <select
            value={preferences.emailDigestFrequency}
            onChange={(e) => updatePreference('emailDigestFrequency', e.target.value)}
            className={selectClass}
            style={selectChevronStyle}
          >
            <option value="immediate">Immediately</option>
            <option value="daily">Daily digest</option>
            <option value="disabled">Disabled</option>
          </select>
        </SettingRow>
      )}

      {/* Events */}
      <SectionLabel>Events</SectionLabel>
      <NotifRow {...rowProps} label="Event created" emailKey="emailOnEventCreated" pushKey="pushOnEventCreated" />
      <NotifRow {...rowProps} label="Event updated" emailKey="emailOnEventUpdated" pushKey="pushOnEventUpdated" />
      <NotifRow {...rowProps} label="Event deleted" emailKey="emailOnEventDeleted" pushKey="pushOnEventDeleted" />
      <NotifRow {...rowProps} label="Member joined" emailKey="emailOnMemberJoined" pushKey="pushOnMemberJoined" />
      <NotifRow {...rowProps} label="Member removed" emailKey="emailOnMemberRemoved" pushKey="pushOnMemberRemoved" />

      {/* Tasks */}
      <SectionLabel>Tasks</SectionLabel>
      <NotifRow {...rowProps} label="Reminder" emailKey="emailOnTaskReminder" pushKey="pushOnTaskReminder" />

      {/* Test buttons */}
      <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--border-primary)]">
        <button
          onClick={sendEmailTest}
          disabled={emailTestState !== 'idle'}
          className={testBtnClass(emailTestState)}
        >
          {testBtnLabel(emailTestState, 'Test email')}
        </button>
        <button
          onClick={sendPushTest}
          disabled={pushTestState !== 'idle' || !pushSupported || !isSubscribed}
          className={testBtnClass(pushTestState)}
        >
          {testBtnLabel(pushTestState, 'Test web push')}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage({
  onClose,
  onOpenShare,
  onOpenImportExport,
  showPastDaysGreyed,
  onShowPastDaysGreyedChange,
  maxEventRows,
  onMaxEventRowsChange,
  autoArchiveEnabled,
  autoArchiveDays,
  onAutoArchiveEnabledChange,
  onAutoArchiveDaysChange,
  calendars,
  events,
  onAddTag,
  onUpdateTag,
  onDeleteTag,
  onEventUpdate,
  refetchTags,
  reorderCalendars,
  selectedCalendar,
  setSelectedCalendar,
  showTodosOnCalendar,
  onShowTodosOnCalendarChange,
  textTags,
  textTagsLoading,
  onCreateTextTag,
  onDeleteTextTag,
  onRenameTextTag,
  initialSection,
}: SettingsPageProps) {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const hasPassword = user?.identities?.some(i => i.provider === 'email') ?? false;
  const { theme, themePreference, setThemePreference, accentColor, setAccentColor } = useTheme();
  const {
    spaces,
    spacesLoading,
    createSpace,
    updateSpace,
    deleteSpace,
    leaveSpace,
    getSpaceInvitations,
    revokeInvitation,
    removeMember,
    updateMemberRole,
  } = useSpace();

  const [openColorPickerId, setOpenColorPickerId] = useState<string | null>(null);

  // Timezone
  const { setting: timezoneSetting, timezone: resolvedTimezone, setTimezone } = useTimezone();

  // Google Calendar sync
  const { connectGoogleCalendar } = useAuth();
  const gcal = useGoogleCalendarSync();
  const [gcalConnecting, setGcalConnecting] = useState(false);
  const [gcalPickerOpen, setGcalPickerOpen] = useState(false);
  const [gcalAvailableCals, setGcalAvailableCals] = useState<{ id: string; summary: string; primary: boolean }[]>([]);
  const [gcalPickerSelected, setGcalPickerSelected] = useState<Set<string>>(new Set());
  const [gcalPickerLoading, setGcalPickerLoading] = useState(false);

  // ── Sidebar section + right-pane breadcrumb stack ──
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection ?? 'account');
  const [navStack, setNavStack] = useState<NavItem[]>([]);
  // On mobile, track whether we're showing the section list or content
  const [mobileShowContent, setMobileShowContent] = useState(false);

  const switchSection = useCallback((section: SettingsSection) => {
    setActiveSection(section);
    setNavStack([]);
    setMobileShowContent(true);
  }, []);

  const pushNav = useCallback((item: NavItem) => {
    setNavStack(prev => [...prev, item]);
  }, []);

  const navigateTo = useCallback((index: number) => {
    setNavStack(prev => prev.slice(0, index));
  }, []);

  const currentSubPage = navStack.length > 0 ? navStack[navStack.length - 1].id : '';

  // ── Profile state ──
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.user_metadata?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  // ── Account / Security state ──
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // ── Preferences state (persisted to localStorage) ──
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(
    localStorage.getItem('calendar-time-format') as '12h' | '24h' || '12h'
  );
  const [dateFormat, setDateFormat] = useState<'MM/DD/YYYY' | 'DD/MM/YYYY'>(
    localStorage.getItem('calendar-date-format') as 'MM/DD/YYYY' | 'DD/MM/YYYY' || 'MM/DD/YYYY'
  );
  const [showWeekNumbers, setShowWeekNumbers] = useState(
    localStorage.getItem('calendar-show-week-numbers') === 'true'
  );
  const [defaultView, setDefaultView] = useState(
    localStorage.getItem('calendar-default-view') || 'year'
  );
  const [privacyMode, setPrivacyMode] = useState(
    localStorage.getItem('calendar-privacy-mode') === 'true'
  );
  const [sidebarHiddenOnStart, setSidebarHiddenOnStart] = useState(
    localStorage.getItem('calendar-sidebar-collapsed') === 'true'
  );
  const [language, setLanguage] = useState(
    localStorage.getItem('calendar-language') || 'en'
  );
  const [taskDefaultView, setTaskDefaultView] = useState(
    localStorage.getItem('tasks-default-view') || 'board'
  );
  const [defaultSpace, setDefaultSpace] = useState(
    localStorage.getItem('calendar-default-space') || 'personal'
  );
  const [defaultEventDuration, setDefaultEventDuration] = useState(
    localStorage.getItem('calendar-default-event-duration') || 'allday'
  );

  // ── Delete account state ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ── Export state ──
  const [exporting, setExporting] = useState(false);

  // ── Space management state ──
  const [newSpaceName, setNewSpaceName] = useState('');
  const [creatingSpace, setCreatingSpace] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('editor');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [pendingInvitations, setPendingInvitations] = useState<import('../../contexts/SpaceContext').SpaceInvitation[]>([]);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  // Local members state for space detail (decoupled from currentSpace)
  const [settingsMembers, setSettingsMembers] = useState<import('../../contexts/SpaceContext').SpaceMember[]>([]);
  const [settingsMembersLoading, setSettingsMembersLoading] = useState(false);
  const [settingsMembersError, setSettingsMembersError] = useState<string | null>(null);
  const [openMemberDropdown, setOpenMemberDropdown] = useState<string | null>(null);

  // ── Category delete confirm ──
  const [deleteTagConfirm, setDeleteTagConfirm] = useState<CalendarType | null>(null);

  // ── Text tags (Tags section) ──
  const [tagUsageCounts, setTagUsageCounts] = useState<Map<number, number>>(new Map());
  const [deleteTextTagConfirm, setDeleteTextTagConfirm] = useState<TextTag | null>(null);
  const [addingTextTag, setAddingTextTag] = useState(false);
  const [newTextTagName, setNewTextTagName] = useState('');
  const addTextTagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (activeSection !== 'tags' || textTags.length === 0) return;
    const load = async () => {
      const ids = textTags.map(t => t.id);
      const [{ data: evLinks }, { data: tdLinks }] = await Promise.all([
        supabase.from('event_tag_links').select('tag_id').in('tag_id', ids),
        supabase.from('todo_tag_links').select('tag_id').in('tag_id', ids),
      ]);
      const counts = new Map<number, number>(ids.map(id => [id, 0]));
      [...(evLinks || []), ...(tdLinks || [])].forEach((r: any) => {
        counts.set(r.tag_id, (counts.get(r.tag_id) ?? 0) + 1);
      });
      setTagUsageCounts(counts);
    };
    load();
  }, [activeSection, textTags.length]);

  // ── Category reorder ──
  const moveTag = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= calendars.length) return;
    await reorderCalendars(index, newIndex);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);


  // ── Handlers ──

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingAvatar(true);
    setAvatarError('');
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const filePath = `${user.id}/avatar.${fileExt}`;
      if (file.size > 2 * 1024 * 1024) throw new Error('File size must be less than 2MB');
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const { error: updateError } = await supabase.auth.updateUser({ data: { avatar_url: publicUrl } });
      if (updateError) throw updateError;
      setAvatarUrl(publicUrl + '?t=' + Date.now());
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to upload avatar.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user) return;
    setUploadingAvatar(true);
    setAvatarError('');
    try {
      const { data: files } = await supabase.storage.from('avatars').list(user.id);
      if (files && files.length > 0) {
        await supabase.storage.from('avatars').remove(files.map(f => `${user.id}/${f.name}`));
      }
      const { error } = await supabase.auth.updateUser({ data: { avatar_url: null } });
      if (error) throw error;
      setAvatarUrl('');
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to delete avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const [uploadingSpaceIcon, setUploadingSpaceIcon] = useState(false);
  const spaceIconInputRef = useRef<HTMLInputElement>(null);

  const handleSpaceIconUpload = async (spaceId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSpaceIcon(true);
    try {
      if (file.size > 2 * 1024 * 1024) throw new Error('File size must be less than 2MB');
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const filePath = `spaces/${spaceId}/icon.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      await updateSpace(spaceId, { icon_url: publicUrl + '?t=' + Date.now() });
    } catch (error) {
      console.error('Error uploading space icon:', error);
    } finally {
      setUploadingSpaceIcon(false);
      e.target.value = '';
    }
  };

  const handleDeleteSpaceIcon = async (spaceId: string) => {
    setUploadingSpaceIcon(true);
    try {
      const { data: files } = await supabase.storage.from('avatars').list(`spaces/${spaceId}`);
      if (files && files.length > 0) {
        await supabase.storage.from('avatars').remove(files.map(f => `spaces/${spaceId}/${f.name}`));
      }
      await updateSpace(spaceId, { icon_url: null });
    } catch (error) {
      console.error('Error deleting space icon:', error);
    } finally {
      setUploadingSpaceIcon(false);
    }
  };

  const handleSaveDisplayName = async () => {
    if (!user) return;
    try {
      await supabase.auth.updateUser({ data: { display_name: displayName } });
    } catch (error) {
      console.error('Error updating display name:', error);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (newPassword.length < 6) { setPasswordError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordSuccess('Password updated. A confirmation email has been sent.');
      setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Failed to update password');
    }
  };

  const handleChangeEmail = async (): Promise<boolean> => {
    setEmailError('');
    setEmailSuccess('');
    if (!newEmail || !newEmail.includes('@')) { setEmailError('Please enter a valid email address'); return false; }
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail }, { emailRedirectTo: `${window.location.origin}/app` });
      if (error) throw error;
      setEmailSuccess('Please confirm the email change by clicking the link sent to your new email.');
      setNewEmail('');
      return true;
    } catch (error) {
      setEmailError(error instanceof Error ? error.message : 'Failed to update email');
      return false;
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    setExporting(true);
    try {
      // GDPR portability means everything the user created, not a selection of
      // it. The previous version exported events, calendars and imported feeds
      // only — leaving out every task, tag, board column, preference and space.
      //
      // Failures are surfaced rather than swallowed: an export that silently
      // omits a table is worse than one that refuses, because the user cannot
      // tell the difference between "no tasks" and "tasks not included".
      const own = (table: string) =>
        supabase.from(table).select('*').eq('user_id', user.id);

      const [
        events, calendars, importedCalendars, importedEvents,
        todos, kanbanColumns, tags, taskSettings, notificationPrefs,
        memberships,
      ] = await Promise.all([
        own('events'),
        own('calendars'),
        own('imported_calendars'),
        own('imported_events'),
        own('todos'),
        own('kanban_columns'),
        own('tags'),
        own('task_settings'),
        own('notification_preferences'),
        supabase.from('space_members').select('*').eq('user_id', user.id),
      ]);

      const results = {
        events, calendars, imported_calendars: importedCalendars,
        imported_events: importedEvents, todos, kanban_columns: kanbanColumns,
        tags, task_settings: taskSettings,
        notification_preferences: notificationPrefs, space_memberships: memberships,
      };

      const failed = Object.entries(results)
        .filter(([, r]) => r.error)
        .map(([k]) => k);
      if (failed.length) {
        throw new Error(`Could not read: ${failed.join(', ')}`);
      }

      // Spaces the user owns, plus their rosters. Space content itself belongs
      // to the space rather than to one member, so it is not duplicated here.
      const { data: ownedSpaces } = await supabase
        .from('spaces').select('*').eq('owner_id', user.id);

      const exportData = {
        exported_at: new Date().toISOString(),
        format_version: 2,
        user: { id: user.id, email: user.email, created_at: user.created_at },
        ...Object.fromEntries(
          Object.entries(results).map(([k, r]) => [k, r.data || []])
        ),
        owned_spaces: ownedSpaces || [],
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mozayc-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast(
        error instanceof Error ? `Export failed: ${error.message}` : 'Export failed',
        'error'
      );
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || !user) return;
    setDeleting(true);
    try {
      // Call edge function to delete all data + auth account (requires service role)
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;

      // Clear local storage
      localStorage.clear();
      await signOut();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast('Failed to delete account. Please try again.', 'error');
      setDeleting(false);
    }
  };

  const persistPref = (key: string, value: string) => localStorage.setItem(key, value);

  const inputClass = 'w-full px-3 py-2 bg-[var(--input-bg)] text-[var(--content-primary)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-sm';

  const accentColors = [
    { name: 'teal', label: 'Teal', light: '#2a9d8f', dark: '#5bc4b5' },
    { name: 'purple', label: 'Purple', light: '#7c3aed', dark: '#a78bfa' },
    { name: 'blue', label: 'Blue', light: '#2563eb', dark: '#60a5fa' },
    { name: 'green', label: 'Green', light: '#059669', dark: '#34d399' },
    { name: 'orange', label: 'Orange', light: '#ea580c', dark: '#fb923c' },
    { name: 'pink', label: 'Pink', light: '#db2777', dark: '#f472b6' },
    { name: 'red', label: 'Red', light: '#dc2626', dark: '#f87171' },
  ];

  // ════════════════════════════════════════════
  // ── ACCOUNT ──
  // ════════════════════════════════════════════

  const renderAccount = () => (
    <div>
      {/* ── General ── */}
      <SectionLabel>General</SectionLabel>

      {/* Avatar + Display Name */}
      <div className="flex items-center gap-4 py-3.5 border-b border-[var(--border-primary)]">
        <div className="relative group/avatar flex-shrink-0">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="w-12 h-12 rounded-full bg-[var(--surface-tertiary)] flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-[var(--content-tertiary)]" />
            )}
          </button>
          {avatarUrl && !uploadingAvatar && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteAvatar(); }}
              className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-primary)] flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity hover:bg-[var(--error)] hover:border-[var(--error)] hover:text-white text-[var(--content-tertiary)]"
              aria-label="Remove avatar"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-[var(--content-tertiary)] ml-0.5 mb-0.5">Display name</p>
          <input
            type="text"
            value={displayName}
            onChange={(e) => { const v = e.target.value; setDisplayName(v.length === 1 ? v.toUpperCase() : v); }}
            onBlur={handleSaveDisplayName}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder="Enter your name"
            className="text-[14px] font-medium text-[var(--content-primary)] bg-transparent w-full rounded-lg px-2 py-1 -ml-2 border border-transparent hover:border-[var(--border-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors"
          />
        </div>
      </div>
      {avatarError && <p className="text-xs text-[var(--error)] py-1">{avatarError}</p>}

      {/* ── Default Space ── */}
      <SectionLabel>Default space</SectionLabel>
      <SettingRow label="Open on startup" description="Which space to load when the app opens">
        <select
          value={defaultSpace}
          onChange={(e) => {
            setDefaultSpace(e.target.value);
            persistPref('calendar-default-space', e.target.value);
            localStorage.removeItem('calendar-current-space');
          }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="personal">Personal</option>
          {spaces.map(sp => (
            <option key={sp.id} value={sp.id}>{sp.name}</option>
          ))}
        </select>
      </SettingRow>

      {/* ── Security ── */}
      <SectionLabel>Security</SectionLabel>

      {/* Email — breadcrumb */}
      <ManageRow label="Email" description={user?.email || 'Your account email address'} buttonLabel="Change email" onClick={() => pushNav({ id: 'change-email', label: 'Change Email' })} />

      {/* Password — breadcrumb */}
      <ManageRow label="Password" description={hasPassword ? 'Change your account password' : 'Set a password for email sign-in'} buttonLabel={hasPassword ? 'Change password' : 'Set password'} onClick={() => pushNav({ id: 'change-password', label: hasPassword ? 'Change Password' : 'Set Password' })} />

      {/* ── Devices ── */}
      <SectionLabel>Devices</SectionLabel>
      <div className="py-3.5 border-b border-[var(--border-primary)]">
        <div className="flex items-center gap-3">
          <Smartphone className="w-4 h-4 text-[var(--content-tertiary)]" />
          <div>
            <p className="text-[13px] text-[var(--content-primary)]">Current session</p>
            <p className="text-xs text-[var(--content-tertiary)] mt-0.5">This browser · Active now</p>
          </div>
        </div>
      </div>

      {/* ── Danger Zone ── */}
      <SectionLabel>Danger zone</SectionLabel>
      <div className="py-3.5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] text-[var(--content-primary)]">Delete account</p>
            <p className="text-xs text-[var(--content-tertiary)] mt-0.5">Permanently delete your account and all data</p>
          </div>
          {!showDeleteConfirm && (
            <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-1.5 text-[13px] text-[var(--error)] border border-[var(--error)]/30 rounded-lg hover:bg-[var(--error)]/5 transition-colors font-medium flex-shrink-0">
              Delete
            </button>
          )}
        </div>
        {showDeleteConfirm && (
          <div className="mt-3 space-y-3 p-4 bg-[var(--error)]/5 rounded-xl border border-[var(--error)]/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[var(--error)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] text-[var(--content-primary)] font-medium">Are you absolutely sure?</p>
                <p className="text-xs text-[var(--content-tertiary)] mt-0.5">Type DELETE to confirm.</p>
              </div>
            </div>
            <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE" className={`${inputClass} border border-[var(--error)]/30`} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} className="px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-primary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-[13px]">
                Cancel
              </button>
              <button onClick={handleDeleteAccount} disabled={deleteConfirmText !== 'DELETE' || deleting} className="px-4 py-2 bg-[var(--error)] text-white rounded-xl hover:opacity-90 transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {deleting ? 'Deleting...' : 'Delete my account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Change Password breadcrumb page ──
  const renderChangePassword = () => (
    <div>
      <div className="space-y-3">
        <div className="relative">
          <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" autoComplete="new-password" className={`${inputClass} pr-10`} />
          <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)]" aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" className={inputClass} />
        {passwordError && <p className="text-xs text-[var(--error)]">{passwordError}</p>}
        {passwordSuccess && <p className="text-xs text-[var(--success)]">{passwordSuccess}</p>}
        <div className="flex justify-end">
          <button onClick={handleChangePassword} disabled={!newPassword || !confirmPassword} className="px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-primary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            Update password
          </button>
        </div>
      </div>
      <p className="text-xs text-[var(--content-tertiary)] mt-4">You will receive a notification email when your password is changed.</p>
    </div>
  );

  // ── Change Email breadcrumb page ──
  const renderChangeEmail = () => (
    <div>
      <div className="space-y-3">
        <div>
          <p className="text-[13px] text-[var(--content-secondary)] mb-2">
            Current email: <strong className="text-[var(--content-primary)]">{user?.email}</strong>
          </p>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" className={inputClass} />
        </div>
        {emailError && <p className="text-xs text-[var(--error)]">{emailError}</p>}
        {emailSuccess && <p className="text-xs text-[var(--success)]">{emailSuccess}</p>}
        <div className="flex justify-end">
          <button onClick={handleChangeEmail} disabled={!newEmail} className="px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-primary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            Update email
          </button>
        </div>
      </div>
      <p className="text-xs text-[var(--content-tertiary)] mt-4">You'll receive a confirmation link at your new email address. The change takes effect after you confirm.</p>
    </div>
  );

  // ════════════════════════════════════════════
  // ── APPEARANCE ──
  // ════════════════════════════════════════════

  const renderAppearance = () => (
    <div>
      <SectionLabel>Default landing page</SectionLabel>
      <SettingRow label="View on startup" description="Which view opens when launching the app">
        <select
          value={defaultView}
          onChange={(e) => {
            setDefaultView(e.target.value);
            persistPref('calendar-default-view', e.target.value);
            localStorage.removeItem('calendar-view-mode');
          }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="todo">Tasks</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
      </SettingRow>

      <SettingRow label="Privacy mode" description="Hide event details on startup" last>
        <Toggle enabled={privacyMode} onToggle={() => { setPrivacyMode(!privacyMode); persistPref('calendar-privacy-mode', String(!privacyMode)); }} />
      </SettingRow>

      <SectionLabel>Sidebar</SectionLabel>
      <SettingRow label="Hidden on start" description="Start with the sidebar collapsed" last>
        <Toggle enabled={sidebarHiddenOnStart} onToggle={() => {
          const next = !sidebarHiddenOnStart;
          setSidebarHiddenOnStart(next);
          persistPref('calendar-sidebar-collapsed', String(next));
        }} />
      </SettingRow>

      <SectionLabel>Theme</SectionLabel>
      <SettingRow label="Color mode" description="Switch between light, dark, or system theme" last>
        <select
          value={themePreference}
          onChange={(e) => setThemePreference(e.target.value as 'light' | 'dark' | 'system')}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="system">System</option>
        </select>
      </SettingRow>

      <SectionLabel>Accent color</SectionLabel>
      <SettingRow label="Color" description="Primary accent color used throughout the app" last>
        <div className="flex items-center gap-1.5">
          {accentColors.map(c => (
            <button
              key={c.name}
              onClick={() => setAccentColor(c.name as any)}
              className={`w-6 h-6 rounded-full transition-all ${accentColor === c.name ? 'ring-2 ring-offset-2 ring-[var(--content-primary)] ring-offset-[var(--surface-secondary)]' : 'hover:scale-110'}`}
              style={{ backgroundColor: theme === 'dark' ? c.dark : c.light }}
              title={c.label}
            />
          ))}
        </div>
      </SettingRow>

      <SectionLabel>Language & time</SectionLabel>
      <SettingRow label="Language" description="App display language" disabled>
        <select
          value={language}
          onChange={(e) => { setLanguage(e.target.value); persistPref('calendar-language', e.target.value); }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="en">English</option>
          <option value="es" disabled>Español (coming soon)</option>
        </select>
      </SettingRow>

      <SettingRow label="Time format" description="12-hour (AM/PM) or 24-hour clock">
        <select
          value={timeFormat}
          onChange={(e) => { setTimeFormat(e.target.value as '12h' | '24h'); persistPref('calendar-time-format', e.target.value); window.dispatchEvent(new Event('mozayc-time-format-changed')); }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="12h">12-hour</option>
          <option value="24h">24-hour</option>
        </select>
      </SettingRow>

      <SettingRow label="Date format" description="Order of day and month in date displays">
        <select
          value={dateFormat}
          onChange={(e) => { setDateFormat(e.target.value as 'MM/DD/YYYY' | 'DD/MM/YYYY'); persistPref('calendar-date-format', e.target.value); }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
        </select>
      </SettingRow>

      <SettingRow label="Timezone" description={timezoneSetting === 'auto' ? `Automatic — ${resolvedTimezone.replace(/_/g, ' ')}` : resolvedTimezone.replace(/_/g, ' ')} last>
        <select
          value={timezoneSetting}
          onChange={(e) => setTimezone(e.target.value)}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="auto">Automatic</option>
          <option value="Pacific/Honolulu">UTC−10 Hawaii</option>
          <option value="America/Anchorage">UTC−9 Alaska</option>
          <option value="America/Los_Angeles">UTC−8 Pacific (LA, Vancouver)</option>
          <option value="America/Denver">UTC−7 Mountain (Denver, Phoenix)</option>
          <option value="America/Chicago">UTC−6 Central (Chicago, Mexico City)</option>
          <option value="America/New_York">UTC−5 Eastern (New York, Toronto)</option>
          <option value="America/Halifax">UTC−4 Atlantic (Halifax)</option>
          <option value="America/Sao_Paulo">UTC−3 São Paulo, Buenos Aires</option>
          <option value="Atlantic/South_Georgia">UTC−2 Mid-Atlantic</option>
          <option value="Atlantic/Azores">UTC−1 Azores</option>
          <option value="Europe/London">UTC+0 London, Lisbon</option>
          <option value="Europe/Paris">UTC+1 Paris, Berlin, Rome</option>
          <option value="Europe/Helsinki">UTC+2 Helsinki, Kyiv, Cairo</option>
          <option value="Europe/Moscow">UTC+3 Moscow, Istanbul, Nairobi</option>
          <option value="Asia/Dubai">UTC+4 Dubai, Baku</option>
          <option value="Asia/Karachi">UTC+5 Karachi, Tashkent</option>
          <option value="Asia/Kolkata">UTC+5:30 Mumbai, Delhi</option>
          <option value="Asia/Kathmandu">UTC+5:45 Kathmandu</option>
          <option value="Asia/Dhaka">UTC+6 Dhaka, Almaty</option>
          <option value="Asia/Yangon">UTC+6:30 Yangon</option>
          <option value="Asia/Bangkok">UTC+7 Bangkok, Jakarta</option>
          <option value="Asia/Shanghai">UTC+8 Shanghai, Singapore, Perth</option>
          <option value="Asia/Tokyo">UTC+9 Tokyo, Seoul</option>
          <option value="Australia/Adelaide">UTC+9:30 Adelaide</option>
          <option value="Australia/Sydney">UTC+10 Sydney, Melbourne</option>
          <option value="Australia/Lord_Howe">UTC+10:30 Lord Howe Island</option>
          <option value="Pacific/Norfolk">UTC+11 Norfolk Island</option>
          <option value="Pacific/Auckland">UTC+12 Auckland, Fiji</option>
          <option value="Pacific/Chatham">UTC+12:45 Chatham Islands</option>
        </select>
      </SettingRow>
    </div>
  );

  // ════════════════════════════════════════════
  // ── CALENDAR ──
  // ════════════════════════════════════════════

  const renderCalendar = () => (
    <div>
      <SettingRow label="Show tasks in calendar" description="Display tasks with due dates on calendar views">
        <Toggle enabled={showTodosOnCalendar} onToggle={() => onShowTodosOnCalendarChange(!showTodosOnCalendar)} />
      </SettingRow>

      <SettingRow label="Visible event rows" description="Rows shown in annual view before overflow">
        <div className="flex items-center gap-2">
          <button onClick={() => onMaxEventRowsChange(Math.max(1, maxEventRows - 1))} disabled={maxEventRows <= 1} className="w-7 h-7 flex items-center justify-center rounded-lg border border-[var(--border-primary)] text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[13px]" aria-label="Decrease event rows">−</button>
          <span className="w-6 text-center text-[13px] font-medium text-[var(--content-primary)]">{maxEventRows}</span>
          <button onClick={() => onMaxEventRowsChange(Math.min(4, maxEventRows + 1))} disabled={maxEventRows >= 4} className="w-7 h-7 flex items-center justify-center rounded-lg border border-[var(--border-primary)] text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[13px]" aria-label="Increase event rows">+</button>
        </div>
      </SettingRow>

      <SettingRow label="Show week numbers" description="Display ISO week numbers in the first column" disabled>
        <Toggle enabled={showWeekNumbers} onToggle={() => { setShowWeekNumbers(!showWeekNumbers); persistPref('calendar-show-week-numbers', String(!showWeekNumbers)); }} />
      </SettingRow>

      <SettingRow label="Default event duration" description="Duration when creating new events">
        <select
          value={defaultEventDuration}
          onChange={(e) => { setDefaultEventDuration(e.target.value); persistPref('calendar-default-event-duration', e.target.value); }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="allday">All day</option>
          <option value="15">15 min</option>
          <option value="30">30 min</option>
          <option value="60">1 hour</option>
          <option value="120">2 hours</option>
        </select>
      </SettingRow>

      <SettingRow label="Grey out past days" description="Dim days that have already passed">
        <Toggle enabled={showPastDaysGreyed} onToggle={() => onShowPastDaysGreyedChange(!showPastDaysGreyed)} />
      </SettingRow>

      <SectionLabel>Google Calendar</SectionLabel>
      {!gcal.connected ? (
        <div className="py-3.5">
          <p className="text-xs text-[var(--content-tertiary)] mb-3">
            Connect your Google account to sync events between Mozayc and Google Calendar.
          </p>
          <button
            onClick={async () => {
              setGcalConnecting(true);
              try {
                await connectGoogleCalendar('settings');
              } catch (e) {
                setGcalConnecting(false);
                toast(e instanceof Error ? e.message : 'Failed to connect Google Calendar', 'error');
              }
            }}
            disabled={gcalConnecting}
            className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded-lg bg-[var(--accent-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Calendar className="w-4 h-4" />
            {gcalConnecting ? 'Connecting...' : 'Connect Google Calendar'}
          </button>
        </div>
      ) : (
        <>
          {/* Connected calendars list */}
          {gcal.syncedCalendars.length > 0 && (
            <div className="py-2 space-y-1">
              {gcal.syncedCalendars.map(cal => (
                <div key={cal.id} className="flex items-center justify-between py-1.5 px-0">
                  <div className="min-w-0">
                    <p className="text-[13px] text-[var(--content-primary)] truncate">{cal.name}</p>
                    {cal.lastSyncedAt && (
                      <p className="text-xs text-[var(--content-tertiary)] mt-0.5">
                        Last synced {new Date(cal.lastSyncedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => gcal.disconnectCalendar(cal.googleCalendarId)}
                    className="ml-3 px-2.5 py-1 text-xs font-medium text-[var(--content-tertiary)] hover:text-[var(--error)] hover:bg-[var(--surface-tertiary)] rounded-lg transition-colors shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add calendar picker */}
          {!gcalPickerOpen ? (
            <div className="py-2">
              <button
                onClick={async () => {
                  setGcalPickerOpen(true);
                  if (gcalAvailableCals.length === 0) {
                    const cals = await gcal.listCalendars();
                    setGcalAvailableCals(cals);
                    // Pre-select calendars not already connected
                    const alreadyConnected = new Set(gcal.syncedCalendars.map(c => c.googleCalendarId));
                    const defaultSelected = cals.filter(c => !alreadyConnected.has(c.id));
                    setGcalPickerSelected(new Set(defaultSelected.map(c => c.id)));
                  }
                }}
                className="text-[13px] font-medium text-[var(--accent-primary)] hover:opacity-80 transition-opacity"
              >
                + Add calendar
              </button>
            </div>
          ) : (
            <div className="py-2 space-y-2">
              {gcalAvailableCals.length === 0 ? (
                <p className="text-xs text-[var(--content-tertiary)]">Loading…</p>
              ) : (
                <>
                  {gcalAvailableCals.map(cal => {
                    const alreadyConnected = gcal.syncedCalendars.some(c => c.googleCalendarId === cal.id);
                    return (
                      <label key={cal.id} className={`flex items-center gap-2 cursor-pointer ${alreadyConnected ? 'opacity-40' : ''}`}>
                        <input
                          type="checkbox"
                          checked={gcalPickerSelected.has(cal.id)}
                          disabled={alreadyConnected}
                          onChange={() => {
                            setGcalPickerSelected(prev => {
                              const next = new Set(prev);
                              next.has(cal.id) ? next.delete(cal.id) : next.add(cal.id);
                              return next;
                            });
                          }}
                          className="rounded border-[var(--border-primary)] accent-[var(--accent-primary)]"
                        />
                        <span className="text-[13px] text-[var(--content-primary)]">
                          {cal.summary}
                          {cal.primary && <span className="ml-1 text-[var(--content-tertiary)]">(primary)</span>}
                          {alreadyConnected && <span className="ml-1 text-[var(--content-tertiary)]">— connected</span>}
                        </span>
                      </label>
                    );
                  })}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={async () => {
                        const toAdd = gcalAvailableCals.filter(c => gcalPickerSelected.has(c.id));
                        if (toAdd.length === 0) { setGcalPickerOpen(false); return; }
                        setGcalPickerLoading(true);
                        try {
                          await gcal.connectCalendars(toAdd);
                          await gcal.pullChanges();
                          setGcalPickerOpen(false);
                          setGcalPickerSelected(new Set());
                        } catch { /* ignore */ } finally {
                          setGcalPickerLoading(false);
                        }
                      }}
                      disabled={gcalPickerLoading || gcalPickerSelected.size === 0}
                      className="px-3 py-1.5 text-[13px] font-medium rounded-lg bg-[var(--accent-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {gcalPickerLoading ? 'Adding…' : 'Add selected'}
                    </button>
                    <button
                      onClick={() => { setGcalPickerOpen(false); setGcalPickerSelected(new Set()); }}
                      className="px-3 py-1.5 text-[13px] text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {gcal.syncEnabled && (
            <SettingRow label="Auto-sync" description="How often to pull changes from Google Calendar">
              <select
                value={gcal.autoSyncInterval}
                onChange={(e) => gcal.setAutoSyncInterval(Number(e.target.value) as 0 | 5 | 15 | 30)}
                className={selectClass}
                style={selectChevronStyle}
              >
                <option value={0}>Manual only</option>
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
              </select>
            </SettingRow>
          )}

          {gcal.syncEnabled && (
            <div className="flex items-center justify-between py-3.5 border-b border-[var(--border-primary)]">
              <p className="text-[13px] text-[var(--content-primary)]">Sync now</p>
              <button
                onClick={async () => {
                  try {
                    await gcal.pullChanges();
                  } catch (e) {
                    toast(e instanceof Error ? e.message : 'Sync failed', 'error');
                  }
                }}
                disabled={gcal.syncing}
                className="px-3 py-1.5 text-[13px] font-medium text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] rounded-lg transition-colors disabled:opacity-40"
              >
                {gcal.syncing ? 'Syncing...' : 'Sync'}
              </button>
            </div>
          )}

          <div className="flex items-center justify-between py-3.5">
            <div className="min-w-0">
              <p className="text-[13px] text-[var(--content-primary)]">Disconnect Google Calendar</p>
              <p className="text-xs text-[var(--content-tertiary)] mt-0.5">Removes synced Google calendars from Mozayc. Doesn't affect your login.</p>
            </div>
            <button
              onClick={() => setConfirmDialog({
                title: 'Disconnect Google Calendar',
                message: "This removes the connection and any synced Google calendars and their events from Mozayc. The events remain on Google. Your login isn't affected.",
                destructive: true,
                onConfirm: () => {
                  gcal.disconnectGoogle((msg) => toast(msg, 'error'));
                  toast('Google Calendar disconnected', 'success');
                },
              })}
              className="ml-3 px-3 py-1.5 text-[13px] font-medium text-[var(--error)] hover:bg-[var(--error)]/5 rounded-lg transition-colors shrink-0"
            >
              Disconnect
            </button>
          </div>
        </>
      )}

      <SectionLabel>Calendars</SectionLabel>
      <ManageRow
        label="Manage calendars"
        description={`${calendars.length} calendar${calendars.length === 1 ? '' : 's'}`}
        buttonLabel="Manage"
        onClick={() => pushNav({ id: 'categories', label: 'Calendars' })}
      />
    </div>
  );

  // ── Categories breadcrumb page ──
  const renderCategories = () => (
    <div>
      <div className="flex flex-col">
        {calendars.map((tag, index) => (
          <div
            key={tag.id}
            className="flex items-center gap-2 group py-0.5 px-1 rounded-lg transition-colors"
          >
            <div className="flex flex-col flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => moveTag(index, -1)}
                disabled={index === 0}
                className="text-[var(--content-tertiary)] hover:text-[var(--content-primary)] disabled:opacity-20 disabled:cursor-not-allowed p-0 leading-none"
                aria-label="Move category up"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => moveTag(index, 1)}
                disabled={index === calendars.length - 1}
                className="text-[var(--content-tertiary)] hover:text-[var(--content-primary)] disabled:opacity-20 disabled:cursor-not-allowed p-0 leading-none"
                aria-label="Move category down"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
            <div className="relative flex-shrink-0">
              <button
                className={`w-5 h-5 rounded-full flex-shrink-0 hover:opacity-80 transition-opacity ${isHexColor(tag.color) ? '' : tag.color}`}
                style={isHexColor(tag.color) ? { backgroundColor: tag.color } : undefined}
                onClick={() => setOpenColorPickerId(openColorPickerId === tag.id ? null : tag.id)}
              />
              {openColorPickerId === tag.id && (
                <div className="absolute left-0 top-full mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl shadow-lg p-2.5 z-[80]" style={{ width: '210px' }}>
                  <ColorPicker
                    value={tag.color}
                    onChange={(c) => { onUpdateTag(tag.id, { color: c }); setOpenColorPickerId(null); }}
                  />
                </div>
              )}
            </div>
            <input
              type="text"
              key={tag.id}
              defaultValue={tag.name}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              onBlur={async (e) => {
                let newName = e.currentTarget.value.trim();
                if (newName.length === 1) newName = newName.toUpperCase();
                if (!newName || newName === tag.name) return;
                await onUpdateTag(tag.id, { name: newName });
              }}
              className="flex-1 min-w-0 px-2 py-0.5 text-[13px] bg-transparent text-[var(--content-primary)] border border-transparent hover:border-[var(--border-primary)] focus:border-[var(--accent-primary)] rounded transition-colors focus:outline-none"
            />
            <button
              onClick={() => setDeleteTagConfirm(tag)}
              className="p-1 text-[var(--content-tertiary)] hover:text-[var(--error)] rounded transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
              aria-label="Delete category"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => onAddTag({ name: 'New Tag', color: COLORS[Math.floor(Math.random() * COLORS.length)].bg })}
        className="mt-2 flex items-center gap-1.5 text-[13px] text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Add category
      </button>

      {/* Delete category confirmation */}
      {deleteTagConfirm && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center animate-fadeInBackdrop" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => setDeleteTagConfirm(null)}>
          <div role="dialog" aria-modal="true" className="bg-[var(--surface-secondary)] rounded-2xl shadow-2xl p-6 w-[340px] animate-fadeIn" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 rounded-lg ${deleteTagConfirm.color} flex items-center justify-center flex-shrink-0`}>
                <Trash2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[var(--content-primary)]">Delete category</p>
                <p className="text-xs text-[var(--content-tertiary)]">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-[13px] text-[var(--content-secondary)] mb-5">
              Are you sure you want to delete <strong>{deleteTagConfirm.name}</strong>? Events using this category will become unassigned.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteTagConfirm(null)} className="px-4 py-2 text-[13px] text-[var(--content-primary)] bg-[var(--surface-tertiary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors">
                Cancel
              </button>
              <button
                onClick={async () => {
                  const tagToDelete = deleteTagConfirm;
                  setDeleteTagConfirm(null);
                  await onDeleteTag(tagToDelete.id);
                  if (selectedCalendar?.id === tagToDelete.id) setSelectedCalendar(calendars[0] || null);
                }}
                className="px-4 py-2 text-[13px] font-medium text-white bg-[var(--error)] rounded-xl hover:opacity-90 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ════════════════════════════════════════════
  // ── NOTIFICATIONS ──
  // ════════════════════════════════════════════

  const renderNotifications = () => <NotificationsSection selectClass={selectClass} selectChevronStyle={selectChevronStyle} />;

  // ════════════════════════════════════════════
  // ── TASKS ──
  // ════════════════════════════════════════════

  const renderTasks = () => (
    <div>
      <SettingRow label="Default view" description="Layout when opening Tasks">
        <select
          value={taskDefaultView}
          onChange={(e) => { setTaskDefaultView(e.target.value); persistPref('tasks-default-view', e.target.value); localStorage.removeItem('todo-display-mode'); }}
          className={selectClass}
          style={selectChevronStyle}
        >
          <option value="board">Board</option>
          <option value="list">List</option>
        </select>
      </SettingRow>

      <div className={`py-3.5 ${autoArchiveEnabled ? '' : ''}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[13px] text-[var(--content-primary)]">Auto-archive completed</p>
            <p className="text-xs text-[var(--content-tertiary)] mt-0.5">Automatically archive completed tasks after the set number of days</p>
          </div>
          <div className="flex-shrink-0">
            <Toggle enabled={autoArchiveEnabled} onToggle={() => onAutoArchiveEnabledChange(!autoArchiveEnabled)} />
          </div>
        </div>
        {autoArchiveEnabled && (
          <div className="flex items-center gap-1.5 mt-3">
            <span className="text-[12px] text-[var(--content-tertiary)] mr-1">After</span>
            {[1, 3, 7, 14, 30].map(d => (
              <button
                key={d}
                onClick={() => onAutoArchiveDaysChange(d)}
                className={`px-2.5 py-1 rounded-lg text-[12px] font-medium transition-colors ${
                  autoArchiveDays === d
                    ? 'bg-[var(--accent-primary)] text-white'
                    : 'bg-[var(--surface-tertiary)] text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)]'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ════════════════════════════════════════════
  // ── SPACES ──
  // ════════════════════════════════════════════

  const renderSpaces = () => (
    <div>
      {/* Create new space */}
      <SectionLabel>Create new space</SectionLabel>
      <div className="flex items-center justify-between py-3.5 border-b border-[var(--border-primary)]">
        <p className="text-[13px] text-[var(--content-primary)]">Start a shared space for your team or family</p>
        <button
          onClick={() => pushNav({ id: 'create-space', label: 'Create Space' })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] rounded-lg transition-colors flex-shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          Create
        </button>
      </div>

      {/* Your spaces */}
      <SectionLabel>Your spaces</SectionLabel>
      <div className="py-2">
        {spacesLoading ? (
          <p className="text-[13px] text-[var(--content-tertiary)] py-2">Loading spaces...</p>
        ) : spaces.length === 0 ? (
          <p className="text-[13px] text-[var(--content-tertiary)] py-2">No shared spaces yet.</p>
        ) : (
          <div className="space-y-1">
            {spaces.map((space) => (
              <button
                key={space.id}
                onClick={async () => {
                  pushNav({ id: `space-${space.id}`, label: space.name });
                  // Load members for this space locally
                  setSettingsMembersLoading(true);
                  setSettingsMembersError(null);
                  setSettingsMembers([]);
                  try {
                    const { data, error } = await supabase.rpc('get_space_members', { p_space_id: space.id });
                    if (error) throw error;
                    setSettingsMembers(data || []);
                  } catch (err) {
                    console.error('Error loading space members:', err);
                    setSettingsMembersError(err instanceof Error ? err.message : 'Failed to load members');
                    setSettingsMembers([]);
                  }
                  setSettingsMembersLoading(false);
                  // Load pending invitations
                  if (space.role === 'owner' || space.role === 'admin') {
                    const invites = await getSpaceInvitations(space.id);
                    setPendingInvitations(invites);
                  }
                }}
                className="no-scale w-full flex items-center justify-between p-3 rounded-xl hover:bg-[var(--surface-tertiary)] transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[var(--surface-tertiary)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {space.icon_url ? (
                      <img src={space.icon_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-4 h-4 text-[var(--content-tertiary)]" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-[13px] font-medium text-[var(--content-primary)]">{space.name}</p>
                    <p className="text-xs text-[var(--content-tertiary)]">
                      {space.member_count} member{space.member_count !== 1 ? 's' : ''}
                      {space.role === 'owner' && <span className="text-amber-500"> · Owner</span>}
                      {space.role !== 'owner' && ` · ${space.role.charAt(0).toUpperCase() + space.role.slice(1)}`}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--content-tertiary)] opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Create Space breadcrumb page ──
  const renderCreateSpace = () => (
    <div>
      <div className="space-y-4">
        <div>
          <label className="text-[13px] text-[var(--content-primary)] block mb-2">Space name</label>
          <input
            type="text"
            value={newSpaceName}
            onChange={(e) => { const v = e.target.value; setNewSpaceName(v.length === 1 ? v.toUpperCase() : v); }}
            placeholder="e.g. Family, Work Team"
            className={inputClass}
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={async () => {
              if (!newSpaceName.trim()) return;
              setCreatingSpace(true);
              const newSpace = await createSpace(newSpaceName.trim());
              if (newSpace) {
                setNewSpaceName('');
                // Fetch members for the new space locally
                try {
                  const { data, error } = await supabase.rpc('get_space_members', { p_space_id: newSpace.id });
                  if (error) throw error;
                  setSettingsMembers(data || []);
                } catch (err) {
                  console.error('Error loading space members:', err);
                  setSettingsMembers([]);
                }
                setNavStack([{ id: `space-${newSpace.id}`, label: newSpace.name }]);
              }
              setCreatingSpace(false);
            }}
            disabled={creatingSpace || !newSpaceName.trim()}
            className="px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-primary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {creatingSpace ? 'Creating...' : 'Create space'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Space Detail breadcrumb page ──
  const renderSpaceDetail = (spaceId: string) => {
    const space = spaces.find(s => s.id === spaceId);
    if (!space) return <p className="text-[13px] text-[var(--content-tertiary)]">Space not found.</p>;

    return (
      <div>
        {/* General — icon + name */}
        {(space.role === 'owner' || space.role === 'admin') && (
          <>
            <SectionLabel>General</SectionLabel>
            <div className="flex items-center gap-4 py-3.5 border-b border-[var(--border-primary)]">
              <div className="relative group/space-icon flex-shrink-0">
                <input type="file" accept="image/*" className="hidden" ref={spaceIconInputRef} onChange={(e) => handleSpaceIconUpload(space.id, e)} />
                <button
                  onClick={() => spaceIconInputRef.current?.click()}
                  disabled={uploadingSpaceIcon}
                  className="w-12 h-12 rounded-full bg-[var(--surface-tertiary)] flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  {space.icon_url ? (
                    <img src={space.icon_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-5 h-5 text-[var(--content-tertiary)]" />
                  )}
                </button>
                {space.icon_url && !uploadingSpaceIcon && (
                  <button
                    onClick={() => handleDeleteSpaceIcon(space.id)}
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-primary)] flex items-center justify-center opacity-0 group-hover/space-icon:opacity-100 transition-opacity hover:bg-[var(--error)] hover:border-[var(--error)] hover:text-white text-[var(--content-tertiary)]"
                    aria-label="Remove space icon"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-[var(--content-tertiary)] ml-0.5 mb-0.5">Space name</p>
                <input
                  type="text"
                  defaultValue={space.name}
                  onBlur={async (e) => {
                    const newName = e.currentTarget.value.trim();
                    if (newName && newName !== space.name) {
                      await updateSpace(space.id, { name: newName });
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  className="text-[14px] font-medium text-[var(--content-primary)] bg-transparent w-full rounded-lg px-2 py-1 -ml-2 border border-transparent hover:border-[var(--border-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors"
                />
              </div>
            </div>
          </>
        )}

        {/* Invite */}
        {(space.role === 'owner' || space.role === 'admin') && (
          <>
            <SectionLabel>Invite people</SectionLabel>
            <div className="py-3.5 border-b border-[var(--border-primary)]">
              <div className="flex gap-2 items-center">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); setInviteSuccess(''); }}
                  placeholder="Email address"
                  className="flex-1 min-w-0 px-3 py-1.5 bg-[var(--input-bg)] text-[var(--content-primary)] rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] placeholder-[var(--content-tertiary)]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inviteEmail.includes('@') && !inviting) {
                      e.preventDefault();
                      document.getElementById('invite-btn')?.click();
                    }
                  }}
                />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'editor' | 'viewer')} className={selectClass} style={selectChevronStyle}>
                  <option value="editor">Can edit</option>
                  <option value="viewer">Can view</option>
                </select>
                <button
                  id="invite-btn"
                  onClick={async () => {
                    if (!inviteEmail.includes('@') || !user) return;
                    setInviting(true); setInviteError(''); setInviteSuccess('');
                    const normalizedEmail = inviteEmail.toLowerCase().trim();
                    // Check if already a member
                    if (settingsMembers.find(m => m.email === normalizedEmail)) {
                      setInviteError('This person is already a member');
                      setInviting(false);
                      return;
                    }
                    if (normalizedEmail === user.email) {
                      setInviteError("You can't invite yourself");
                      setInviting(false);
                      return;
                    }
                    try {
                      const expiresAt = new Date();
                      expiresAt.setDate(expiresAt.getDate() + 7);
                      const { data: insertedInvite, error } = await supabase
                        .from('space_invitations')
                        .insert({
                          space_id: space.id, email: normalizedEmail, role: inviteRole,
                          invited_by: user.id, expires_at: expiresAt.toISOString(),
                        })
                        .select('token')
                        .single();
                      if (error) {
                        if (error.code === '23505') { setInviteError('This person already has a pending invitation'); }
                        else { setInviteError(error.message); }
                      } else {
                        // Send invitation email with token so the link goes to /invite?token=...
                        const inviterName = user.user_metadata?.display_name || user.email;
                        const roleLabel = inviteRole === 'editor' ? 'Can edit' : 'Can view';
                        supabase.functions.invoke('send-invite-email', {
                          body: { to: normalizedEmail, spaceName: space.name, role: roleLabel, invitedByName: inviterName, inviteToken: insertedInvite.token },
                        }).catch(() => {});
                        setInviteSuccess(`Invitation sent to ${inviteEmail}`);
                        setInviteEmail('');
                        const invites = await getSpaceInvitations(space.id);
                        setPendingInvitations(invites);
                      }
                    } catch (err) {
                      setInviteError(err instanceof Error ? err.message : 'Failed to invite');
                    }
                    setInviting(false);
                  }}
                  disabled={inviting || !inviteEmail.includes('@')}
                  className="px-3 py-1.5 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-hover)] transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 flex-shrink-0"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {inviting ? 'Inviting...' : 'Invite'}
                </button>
              </div>
              {inviteError && <p className="text-xs text-[var(--error)] mt-1.5">{inviteError}</p>}
              {inviteSuccess && <p className="text-xs text-[var(--success)] mt-1.5">{inviteSuccess}</p>}
            </div>

            {/* Pending invitations */}
            {pendingInvitations.length > 0 && (
              <>
                <SectionLabel>Pending invitations</SectionLabel>
                <div className="py-2">
                  {pendingInvitations.map((inv) => {
                    const daysLeft = Math.max(0, Math.ceil((new Date(inv.expires_at).getTime() - Date.now()) / 86400000));
                    return (
                      <div key={inv.id} className="flex items-center justify-between py-2 group">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-6 h-6 rounded-full bg-[var(--surface-tertiary)] flex items-center justify-center flex-shrink-0">
                            <Mail className="w-3 h-3 text-[var(--content-tertiary)]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] text-[var(--content-primary)] truncate">{inv.email}</p>
                            <p className="text-[11px] text-[var(--content-tertiary)]">
                              {inv.role} · {daysLeft === 0 ? 'expires today' : `${daysLeft}d left`}
                            </p>
                          </div>
                        </div>
                        {revokeConfirmId === inv.id ? (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[11px] text-[var(--content-tertiary)]">Revoke?</span>
                            <button
                              onClick={async () => {
                                await revokeInvitation(inv.id);
                                setPendingInvitations(prev => prev.filter(i => i.id !== inv.id));
                                setRevokeConfirmId(null);
                              }}
                              className="px-2 py-0.5 text-[11px] font-medium text-white bg-[var(--error)] rounded-md hover:opacity-90 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setRevokeConfirmId(null)}
                              className="px-2 py-0.5 text-[11px] font-medium text-[var(--content-secondary)] bg-[var(--surface-tertiary)] rounded-md hover:bg-[var(--sidebar-hover)] transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRevokeConfirmId(inv.id)}
                            className="p-1 text-[var(--content-tertiary)] hover:text-[var(--error)] rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                            title="Revoke invitation"
                            aria-label="Revoke invitation"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* Members */}
        <SectionLabel>Members</SectionLabel>
        <div className="py-3.5 border-b border-[var(--border-primary)]">
          {settingsMembersLoading ? (
            <p className="text-xs text-[var(--content-tertiary)]">Loading...</p>
          ) : settingsMembersError ? (
            <p className="text-xs text-[var(--error)]">{settingsMembersError}</p>
          ) : settingsMembers.length === 0 ? (
            <p className="text-xs text-[var(--content-tertiary)]">No members found.</p>
          ) : (
            <div className="space-y-1">
              {settingsMembers.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[var(--accent-primary)] flex items-center justify-center text-white text-[11px] font-medium flex-shrink-0">
                        {(member.display_name || member.email)?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[13px] text-[var(--content-primary)] truncate leading-tight">
                        {member.display_name || member.email}
                      </p>
                      {member.display_name && (
                        <p className="text-[11px] text-[var(--content-tertiary)] truncate leading-tight mt-0.5">
                          {member.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    {member.is_owner ? (
                      <span className="text-[11px] text-[var(--content-tertiary)] font-medium">Owner</span>
                    ) : (space.role === 'owner' || space.role === 'admin') ? (
                      <div className="relative">
                        <button
                          onClick={() => setOpenMemberDropdown(prev => prev === member.user_id ? null : member.user_id)}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] text-[var(--content-secondary)] bg-[var(--surface-tertiary)] hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
                        >
                          {member.role === 'editor' ? 'Can edit' : member.role === 'admin' ? 'Admin' : 'Can view'}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {openMemberDropdown === member.user_id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMemberDropdown(null)} />
                            <div className="absolute right-0 top-full mt-1 w-52 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                              {([
                                { role: 'editor', label: 'Can edit', desc: 'Can create, edit, and delete events' },
                                { role: 'viewer', label: 'Can view', desc: 'Can view events only' },
                              ] as const).map(({ role, label, desc }) => (
                                <button
                                  key={role}
                                  onClick={async () => {
                                    setOpenMemberDropdown(null);
                                    await updateMemberRole(member.user_id, role as 'admin' | 'editor' | 'viewer', space.id);
                                    setSettingsMembers(prev => prev.map(m => m.user_id === member.user_id ? { ...m, role } : m));
                                  }}
                                  className="w-full flex items-start gap-2.5 px-3 py-2 hover:bg-[var(--sidebar-hover)] transition-colors text-left"
                                >
                                  <div className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 flex items-center justify-center">
                                    {member.role === role && <Check className="w-3.5 h-3.5 text-[var(--accent-primary)]" />}
                                  </div>
                                  <div>
                                    <p className="text-[12px] font-medium text-[var(--content-primary)] leading-tight">{label}</p>
                                    <p className="text-[11px] text-[var(--content-tertiary)] leading-tight mt-0.5">{desc}</p>
                                  </div>
                                </button>
                              ))}
                              <div className="border-t border-[var(--border-primary)] my-1" />
                              <button
                                onClick={() => {
                                  setOpenMemberDropdown(null);
                                  setConfirmDialog({
                                    title: 'Remove member',
                                    message: `Remove ${member.display_name || member.email} from this space?`,
                                    destructive: true,
                                    onConfirm: async () => {
                                      // Via the context, so SpaceContext.members
                                      // updates (the sidebar roster) and the
                                      // removal notification is sent.
                                      await removeMember(member.user_id, space.id);
                                      setSettingsMembers(prev => prev.filter(m => m.user_id !== member.user_id));
                                    }
                                  });
                                }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--error)]/8 transition-colors text-left"
                              >
                                <div className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                                  <Trash2 className="w-3.5 h-3.5 text-[var(--error)]" />
                                </div>
                                <p className="text-[12px] font-medium text-[var(--error)] leading-tight">Remove member</p>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-[var(--content-tertiary)]">
                        {member.role === 'editor' ? 'Can edit' : 'Can view'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <SectionLabel>Danger zone</SectionLabel>
        <div className="flex items-center justify-between py-3.5">
          <div>
            <p className="text-[13px] text-[var(--content-primary)]">{space.role === 'owner' ? 'Delete space' : 'Leave space'}</p>
            <p className="text-xs text-[var(--content-tertiary)] mt-0.5">{space.role === 'owner' ? 'This cannot be undone' : 'You can rejoin with an invite link'}</p>
          </div>
          {space.role === 'owner' ? (
            <button
              onClick={() => setConfirmDialog({ title: 'Delete space', message: `Delete "${space.name}"? All events, members, and invitations will be permanently removed.`, destructive: true, onConfirm: async () => { await deleteSpace(space.id); navigateTo(0); } })}
              className="px-3 py-1.5 text-[13px] text-[var(--error)] border border-[var(--error)]/30 rounded-lg hover:bg-[var(--error)]/5 transition-colors font-medium flex-shrink-0"
            >
              Delete
            </button>
          ) : (
            <button
              onClick={() => setConfirmDialog({ title: 'Leave space', message: `Leave "${space.name}"? You'll need a new invitation to rejoin.`, onConfirm: async () => { await leaveSpace(space.id); navigateTo(0); } })}
              className="px-3 py-1.5 text-[13px] text-[var(--error)] border border-[var(--error)]/30 rounded-lg hover:bg-[var(--error)]/5 transition-colors font-medium flex-shrink-0"
            >
              Leave
            </button>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════
  // ── DATA & PRIVACY ──
  // ════════════════════════════════════════════

  const renderData = () => (
    <div>
      <SectionLabel>Export</SectionLabel>
      <ManageRow label="Export your data" description="Events, calendars, and settings" buttonLabel="Export" onClick={() => pushNav({ id: 'export', label: 'Export' })} />

      <SectionLabel>Import</SectionLabel>
      <SectionLabel>Print settings</SectionLabel>
      <div className="py-3.5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] text-[var(--content-primary)]">Default print settings</p>
            <p className="text-xs text-[var(--content-tertiary)] mt-0.5">Configure print layout for any view</p>
          </div>
          <span className="text-[13px] text-[var(--content-tertiary)] italic">Coming soon</span>
        </div>
      </div>
    </div>
  );

  // ── Export breadcrumb page ──
  const renderExport = () => (
    <div>
      <SectionLabel>Format</SectionLabel>
      <div className="py-3.5 border-b border-[var(--border-primary)]">
        <div className="flex gap-2">
          {['JSON', 'CSV', 'PDF'].map((fmt) => (
            <button
              key={fmt}
              // CSV and PDF are not implemented. Rendering them as live buttons
              // that do nothing is worse than showing them as unavailable.
              disabled={fmt !== 'JSON'}
              className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                fmt === 'JSON'
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--surface-tertiary)] text-[var(--content-tertiary)] opacity-50 cursor-not-allowed'
              }`}
            >
              {fmt}
            </button>
          ))}
        </div>
        <p className="text-xs text-[var(--content-tertiary)] mt-2 italic">CSV and PDF export coming soon</p>
      </div>

      <SectionLabel>Options</SectionLabel>
      <div className="py-3.5 border-b border-[var(--border-primary)]">
        <p className="text-xs text-[var(--content-tertiary)]">Date range and space selection coming soon. Currently exports all data.</p>
      </div>

      <div className="pt-4 flex justify-end">
        <button
          onClick={handleExportData}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-primary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-[13px] font-medium disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting...' : 'Export all data (JSON)'}
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════
  // ── TAGS ──
  // ════════════════════════════════════════════

  const renderTags = () => (
    <div>
      <SectionLabel>Your tags</SectionLabel>
      {textTagsLoading ? (
        <p className="text-[13px] text-[var(--content-tertiary)] py-4">Loading tags...</p>
      ) : textTags.length === 0 && !addingTextTag ? (
        <p className="text-[13px] text-[var(--content-tertiary)] py-4">No tags yet.</p>
      ) : (
        <div className="flex flex-col">
          {textTags.map((tag) => {
            const count = tagUsageCounts.get(tag.id) ?? 0;
            return (
              <div key={tag.id} className="flex items-center gap-2 group py-0.5 px-1 rounded-lg">
                <Hash className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
                <input
                  type="text"
                  defaultValue={tag.name}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  onBlur={async (e) => {
                    const newName = e.currentTarget.value.trim();
                    if (!newName || newName === tag.name) return;
                    await onRenameTextTag(tag.id, newName);
                  }}
                  className="flex-1 min-w-0 px-2 py-0.5 text-[13px] bg-transparent text-[var(--content-primary)] border border-transparent hover:border-[var(--border-primary)] focus:border-[var(--accent-primary)] rounded transition-colors focus:outline-none"
                />
                {count > 0 && (
                  <span className="text-[11px] text-[var(--content-tertiary)] flex-shrink-0 tabular-nums">
                    {count}
                  </span>
                )}
                <button
                  onClick={() => setDeleteTextTagConfirm(tag)}
                  className="p-1 text-[var(--content-tertiary)] hover:text-[var(--error)] rounded transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                  aria-label="Delete tag"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}

          {addingTextTag && (
            <div className="flex items-center gap-2 mt-0.5 px-1">
              <Hash className="w-3.5 h-3.5 text-[var(--accent-primary)] flex-shrink-0" />
              <input
                ref={addTextTagInputRef}
                type="text"
                value={newTextTagName}
                onChange={(e) => setNewTextTagName(e.target.value)}
                placeholder="tag name"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newTextTagName.trim()) {
                    await onCreateTextTag(newTextTagName.trim());
                    setNewTextTagName('');
                    setAddingTextTag(false);
                  } else if (e.key === 'Escape') {
                    setNewTextTagName('');
                    setAddingTextTag(false);
                  }
                }}
                onBlur={() => { setNewTextTagName(''); setAddingTextTag(false); }}
                className="flex-1 min-w-0 px-2 py-0.5 text-[13px] bg-transparent text-[var(--content-primary)] border border-[var(--accent-primary)] rounded focus:outline-none"
              />
            </div>
          )}
        </div>
      )}

      {!addingTextTag && (
        <button
          onClick={() => {
            setAddingTextTag(true);
            requestAnimationFrame(() => addTextTagInputRef.current?.focus());
          }}
          className="mt-2 flex items-center gap-1.5 text-[13px] text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add tag
        </button>
      )}

      {deleteTextTagConfirm && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center animate-fadeInBackdrop"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setDeleteTextTagConfirm(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-[var(--surface-secondary)] rounded-2xl shadow-2xl p-6 w-[340px] animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[var(--error)]/10 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-4 h-4 text-[var(--error)]" />
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[var(--content-primary)]">Delete tag</p>
                <p className="text-xs text-[var(--content-tertiary)]">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-[13px] text-[var(--content-secondary)] mb-5">
              Delete <strong>{deleteTextTagConfirm.name}</strong>?
              {(() => {
                const count = tagUsageCounts.get(deleteTextTagConfirm.id) ?? 0;
                return count > 0
                  ? ` It's used on ${count} item${count === 1 ? '' : 's'} and will be removed from all of them.`
                  : '';
              })()}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteTextTagConfirm(null)}
                className="px-4 py-2 text-[13px] text-[var(--content-primary)] bg-[var(--surface-tertiary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const tag = deleteTextTagConfirm;
                  setDeleteTextTagConfirm(null);
                  await onDeleteTextTag(tag.id);
                }}
                className="px-4 py-2 text-[13px] font-medium text-white bg-[var(--error)] rounded-xl hover:opacity-90 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ════════════════════════════════════════════
  // ── PAGE ROUTER ──
  // ════════════════════════════════════════════

  const sectionTitles: Record<SettingsSection, { title: string; description: string }> = {
    account: { title: 'Account', description: 'Manage your profile and security' },
    appearance: { title: 'Appearance', description: 'Customize how the app looks and feels' },
    calendar: { title: 'Calendar', description: 'Configure calendar behavior' },
    notifications: { title: 'Notifications', description: 'Control email and push notifications' },
    tasks: { title: 'Tasks', description: 'Task board settings and automation' },
    spaces: { title: 'Spaces', description: 'Shared calendars for teams and families' },
    tags: { title: 'Tags', description: 'Manage labels used on events and tasks' },
    data: { title: 'Data & Privacy', description: 'Export, import, and data management' },
  };

  const subPageTitles: Record<string, { title: string; description: string }> = {
    'categories': { title: 'Calendars', description: 'Create, rename, and reorder your calendars' },
    'create-space': { title: 'Create Space', description: 'Set up a new shared space' },
    'export': { title: 'Export', description: 'Download your data' },
    'change-email': { title: 'Change Email', description: 'Update your account email address' },
    'change-password': { title: hasPassword ? 'Change Password' : 'Set Password', description: hasPassword ? 'Update your existing password' : 'Set a password for email sign-in' },
  };

  const getPageInfo = (): { title: string; description: string } => {
    if (!currentSubPage) return sectionTitles[activeSection];
    if (subPageTitles[currentSubPage]) return subPageTitles[currentSubPage];
    if (currentSubPage.startsWith('space-')) {
      const spaceId = currentSubPage.replace('space-', '');
      const space = spaces.find(s => s.id === spaceId);
      return { title: space?.name || 'Space', description: 'Manage space settings and members' };
    }
    return sectionTitles[activeSection];
  };

  const { title: currentTitle, description: currentDescription } = getPageInfo();

  const renderContentPane = () => {
    if (currentSubPage) {
      if (currentSubPage === 'categories') return renderCategories();
      if (currentSubPage === 'create-space') return renderCreateSpace();
      if (currentSubPage === 'export') return renderExport();
      if (currentSubPage === 'change-email') return renderChangeEmail();
      if (currentSubPage === 'change-password') return renderChangePassword();
      if (currentSubPage.startsWith('space-')) return renderSpaceDetail(currentSubPage.replace('space-', ''));
      return null;
    }
    switch (activeSection) {
      case 'account': return renderAccount();
      case 'appearance': return renderAppearance();
      case 'calendar': return renderCalendar();
      case 'notifications': return renderNotifications();
      case 'tasks': return renderTasks();
      case 'spaces': return renderSpaces();
      case 'tags': return renderTags();
      case 'data': return renderData();
    }
  };

  const sections: { id: SettingsSection; label: string; icon: React.ElementType }[] = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'tasks', label: 'Tasks', icon: ListTodo },
    { id: 'spaces', label: 'Spaces', icon: Users },
    { id: 'tags', label: 'Tags', icon: Hash },
    { id: 'data', label: 'Data & Privacy', icon: Database },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-fadeInBackdrop"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* ── MOBILE LAYOUT ── */}
      <div
        role="dialog"
        aria-modal="true"
        className="sm:hidden bg-[var(--surface-secondary)] w-full flex flex-col animate-slideUp rounded-t-2xl overflow-hidden"
        style={{ height: '92dvh', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-[var(--content-tertiary)] opacity-40" />
        </div>

        {!mobileShowContent ? (
          /* Section list */
          <>
            <div className="px-5 pt-3 pb-2 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold text-[var(--content-primary)]">Settings</h2>
              <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--content-tertiary)] active:bg-[var(--sidebar-hover)] transition-colors" aria-label="Close settings">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => switchSection(section.id)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left active:bg-[var(--sidebar-hover)] transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-[var(--surface-tertiary)] flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-[18px] h-[18px] text-[var(--content-secondary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--content-primary)]">{sectionTitles[section.id].title}</p>
                    <p className="text-xs text-[var(--content-tertiary)]">{sectionTitles[section.id].description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--content-tertiary)] flex-shrink-0" />
                </button>
              ))}
            </div>
          </>
        ) : (
          /* Section content */
          <>
            <div className="px-4 pt-2 pb-2 flex items-center gap-2 flex-shrink-0 border-b border-[var(--border-primary)]">
              <button
                onClick={() => { if (navStack.length > 0) { setNavStack(prev => prev.slice(0, -1)); } else { setMobileShowContent(false); } }}
                className="p-1.5 rounded-lg text-[var(--content-secondary)] active:bg-[var(--sidebar-hover)] transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-[var(--content-primary)] truncate">{currentTitle}</h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--content-tertiary)] active:bg-[var(--sidebar-hover)] transition-colors" aria-label="Close settings">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-styled">
              <div className="px-5 py-4">
                {renderContentPane()}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── DESKTOP LAYOUT ── */}
      <div
        role="dialog"
        aria-modal="true"
        className="hidden sm:flex bg-[var(--surface-secondary)] rounded-2xl shadow-2xl overflow-hidden animate-fadeIn"
        style={{ width: '90%', maxWidth: '880px', height: '88vh', maxHeight: '780px' }}
      >
        {/* Sidebar */}
        <div className="w-52 bg-[var(--sidebar-bg)] border-r border-[var(--border-primary)] flex flex-col flex-shrink-0">
          <div className="px-5 pt-5 pb-3">
            <h2 className="text-[13px] font-semibold text-[var(--content-primary)]">Settings</h2>
          </div>
          <nav className="flex-1 px-2 overflow-y-auto scrollbar-styled">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => switchSection(section.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] transition-colors ${
                  activeSection === section.id
                    ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-medium'
                    : 'text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--content-primary)]'
                }`}
              >
                <section.icon className="w-[15px] h-[15px]" />
                <span>{section.label}</span>
              </button>
            ))}
          </nav>
          <div className="px-2 pb-3 pt-1 border-t border-[var(--border-primary)]">
            <button onClick={signOut} className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--content-primary)] transition-colors">
              <LogOut className="w-[15px] h-[15px]" />
              Sign out
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg text-[var(--content-tertiary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--content-primary)] transition-colors z-10" aria-label="Close settings">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>

          {/* Header with breadcrumb + title */}
          <div className="px-8 pt-7 pb-1 flex-shrink-0">
            {navStack.length > 0 && (
              <div className="flex items-center gap-1 text-[12px] mb-2">
                <button onClick={() => navigateTo(0)} className="text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors">
                  {sectionTitles[activeSection].title}
                </button>
                {navStack.map((item, i) => (
                  <React.Fragment key={item.id}>
                    <ChevronRight className="w-3 h-3 text-[var(--content-tertiary)] flex-shrink-0" />
                    <button
                      onClick={() => navigateTo(i + 1)}
                      className={`transition-colors ${i === navStack.length - 1 ? 'text-[var(--content-primary)] font-medium' : 'text-[var(--content-tertiary)] hover:text-[var(--content-primary)]'}`}
                    >
                      {item.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
            <h1 className="text-lg font-semibold text-[var(--content-primary)]">{currentTitle}</h1>
            {currentDescription && <p className="text-xs text-[var(--content-tertiary)] mt-0.5">{currentDescription}</p>}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-auto scrollbar-styled">
            <div className="px-8 py-5">
              {renderContentPane()}
            </div>
          </div>
        </div>
      </div>

      {confirmDialog && (
        <ConfirmDialog dialog={confirmDialog} onClose={() => setConfirmDialog(null)} />
      )}
    </div>
  );
}
